import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowDownCircle,
  ArrowUpCircle,
  CreditCard,
  RefreshCw,
  Target,
  TrendingUp,
  Users,
  Wallet,
  X
} from "lucide-react";
import API from "../services/api";
import AdminUserLink from "../components/AdminUserLink";
import AdminLayout from "./AdminLayout";

const emptyStats = {
  totalDeposit: 0,
  totalWithdraw: 0,
  totalUsers: 0,
  todayTotalBids: 0,
  totalWalletBalance: 0,
  activePlayers: 0,
};

const emptyAnalytics = {
  activePlayers: 0,
  betTypes: [],
  cashFlow: [],
  topDepositorsToday: [],
  topDepositorsAll: [],
  topWithdrawersToday: [],
  topWithdrawersAll: [],
};

function AdminDashboard() {
  const [stats, setStats] = useState(emptyStats);
  const [analytics, setAnalytics] = useState(emptyAnalytics);
  const [transactions, setTransactions] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [withdraws, setWithdraws] = useState([]);
  const [rankingMode, setRankingMode] = useState("today_deposit");
  const [rankingExpanded, setRankingExpanded] = useState(false);
  const [walletTarget, setWalletTarget] = useState(null);
  const [walletForm, setWalletForm] = useState({ mode: "deposit", amount: "", remark: "" });

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsRes, analyticsRes, txRes, depRes, wdRes] = await Promise.all([
        API.get("/admin/stats"),
        API.get("/admin/dashboard-analytics"),
        API.get("/admin/transactions"),
        API.get("/admin/deposits"),
        API.get("/admin/withdraws")
      ]);

      setStats({ ...emptyStats, ...(statsRes.data || {}) });
      setAnalytics({ ...emptyAnalytics, ...(analyticsRes.data || {}) });
      setTransactions(Array.isArray(txRes.data) ? txRes.data : []);
      setDeposits(Array.isArray(depRes.data) ? depRes.data : []);
      setWithdraws(Array.isArray(wdRes.data) ? wdRes.data : []);
    } catch (err) {
      console.log(err);
    }
  };

  const pendingDeposits = deposits.filter((item) => item.status === "pending");
  const pendingWithdraws = withdraws.filter((item) => item.status === "pending");
  const netProfit = Number(stats.totalDeposit || 0) - Number(stats.totalWithdraw || 0);
  const recentActivity = [...transactions].slice(0, 8);

  const openWalletAction = (user, mode = "deposit") => {
    setWalletTarget(user);
    setWalletForm({
      mode,
      amount: "",
      remark: mode === "deposit" ? "Dashboard top depositor deposit" : "Dashboard top D/W withdraw",
    });
  };

  const submitWalletAction = async () => {
    if (!walletTarget?.user_id || !walletForm.amount || Number(walletForm.amount) <= 0) return;
    try {
      await API.post("/admin/users/manual-transaction", {
        user_id: walletTarget.user_id,
        mode: walletForm.mode,
        amount: Number(walletForm.amount),
        remark: walletForm.remark,
      });
      setWalletTarget(null);
      setWalletForm({ mode: "deposit", amount: "", remark: "" });
      await loadDashboard();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to update wallet");
    }
  };

  const cards = useMemo(() => [
    {
      title: "Today Total Bids",
      value: formatCompact(stats.todayTotalBids),
      caption: "Placed today",
      delta: "Live",
      color: "#316ce8",
      icon: Activity
    },
    {
      title: "Active Players",
      value: formatCompact(analytics.activePlayers || stats.activePlayers),
      caption: "Players with bets today",
      delta: "Today",
      color: "#16a34a",
      icon: Target
    },
    {
      title: "Total Users",
      value: formatCompact(stats.totalUsers),
      caption: "Registered users",
      delta: `${pendingWithdraws.length} payouts`,
      color: "#8b3df0",
      icon: Users
    },
    {
      title: "Wallet Balance",
      value: formatMoney(stats.totalWalletBalance),
      caption: "Current user balance",
      delta: `${pendingDeposits.length} deposits`,
      color: "#0f9f70",
      icon: CreditCard
    },
    {
      title: "Net D/W",
      value: formatMoney(netProfit),
      caption: "Deposit - Withdraw",
      delta: "Real D/W",
      color: netProfit >= 0 ? "#316ce8" : "#ef4444",
      icon: TrendingUp
    }
  ], [analytics.activePlayers, netProfit, pendingDeposits.length, pendingWithdraws.length, stats]);

  return (
    <AdminLayout>
      <section style={dashboardWrap}>
        <div style={headingRow}>
          <div>
            <div style={titleRow}>
              <h1 style={title}>Dashboard</h1>
              <span style={liveBadge}>LIVE</span>
            </div>
            <p style={subtitle}>Real data from users, bids, deposits and withdraws</p>
          </div>

          <button type="button" style={refreshButton} onClick={loadDashboard}>
            <RefreshCw size={16} />
          </button>
        </div>

        <div style={kpiGrid} className="admin-dashboard-kpis">
          {cards.map((card) => (
            <KpiCard key={card.title} {...card} />
          ))}
        </div>

        <div style={miniGrid} className="admin-dashboard-minis">
          <MiniMetric icon={ArrowDownCircle} title={formatMoney(stats.totalWithdraw)} label="Total Withdrawals" tone="#fef2f2" color="#ef4444" />
          <MiniMetric icon={ArrowUpCircle} title={formatMoney(stats.totalDeposit)} label="Total Deposits" tone="#ecfdf5" color="#10b981" />
          <MiniMetric icon={CreditCard} title={pendingDeposits.length} label="Pending Deposits" tone="#eff6ff" color="#2563eb" />
          <MiniMetric icon={Activity} title={pendingWithdraws.length} label="Pending Payouts" tone="#fffbeb" color="#f59e0b" />
        </div>

        <div style={analyticsGrid} className="admin-dashboard-analytics">
          <BidsByGame data={analytics.betTypes || []} total={stats.todayTotalBids} />
          <CashFlow data={analytics.cashFlow || []} />
        </div>

        <div style={panelGrid} className="admin-dashboard-panels">
          <section style={panel}>
            <header style={panelHeader}>
              <div style={panelTitleWrap}>
                <span style={panelIcon}><Activity size={14} /></span>
                <span>
                  <strong style={panelTitle}>Recent Transactions</strong>
                  <small style={panelSubtitle}>Live admin activity</small>
                </span>
              </div>
            </header>

            <div style={activityList}>
              {recentActivity.map((item) => (
                <div key={`${item.id}-${item.type}`} style={activityRow}>
                  <div>
                    <AdminUserLink
                      userId={item.user_id}
                      name={item.user_name || item.user_phone || `User ${item.user_id}`}
                      phone={item.user_phone}
                      style={activityUserLink}
                    />
                    <div style={activitySub}>{item.type} - {item.remark || item.status || "-"}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <strong style={Number(item.amount || 0) >= 0 ? amountGreen : amountRed}>
                      {Number(item.amount || 0) >= 0 ? "+" : "-"}{formatMoney(Math.abs(Number(item.amount || 0)))}
                    </strong>
                    <div style={activitySub}>{formatDateTime(item.created_at)}</div>
                  </div>
                </div>
              ))}
              {!recentActivity.length && <div style={emptyPanel}>No activity yet</div>}
            </div>
          </section>

          <TopDwPanel
            analytics={analytics}
            mode={rankingMode}
            onModeChange={(value) => { setRankingMode(value); setRankingExpanded(false); }}
            expanded={rankingExpanded}
            onToggleExpanded={() => setRankingExpanded((value) => !value)}
            onWallet={openWalletAction}
          />
        </div>
        {walletTarget && (
          <div style={walletBackdrop} className="admin-keyboard-wallet-overlay" onClick={() => setWalletTarget(null)}>
            <section style={walletModal} className="admin-keyboard-wallet-modal" onClick={(event) => event.stopPropagation()}>
              <header style={walletModalHeader} className="admin-keyboard-wallet-header">
                <strong>{walletTarget.user_name || `User ${walletTarget.user_id}`}</strong>
                <button type="button" style={walletCloseButton} onClick={() => setWalletTarget(null)}><X size={18} /></button>
              </header>
              <div style={walletBody} className="admin-keyboard-wallet-body">
                <div style={walletModeRow} className="admin-keyboard-wallet-modes">
                  <button type="button" style={walletForm.mode === "deposit" ? activeWalletMode : walletModeButton} onClick={() => setWalletForm((current) => ({ ...current, mode: "deposit" }))}>Deposit</button>
                  <button type="button" style={walletForm.mode === "withdraw" ? activeWalletModeRed : walletModeButton} onClick={() => setWalletForm((current) => ({ ...current, mode: "withdraw" }))}>Withdraw</button>
                </div>
                <input
                  style={walletInput}
                  className="admin-keyboard-wallet-input"
                  type="number"
                  min="1"
                  value={walletForm.amount}
                  inputMode="decimal"
                  enterKeyHint="done"
                  autoComplete="off"
                  onChange={(event) => setWalletForm((current) => ({ ...current, amount: event.target.value }))}
                  placeholder="Amount"
                />
                <input
                  style={walletInput}
                  className="admin-keyboard-wallet-input"
                  value={walletForm.remark}
                  enterKeyHint="done"
                  autoComplete="off"
                  onChange={(event) => setWalletForm((current) => ({ ...current, remark: event.target.value }))}
                  placeholder="Remark"
                />
                <button type="button" style={walletSubmitButton} className="admin-keyboard-wallet-submit" onClick={submitWalletAction}>Save Wallet Entry</button>
              </div>
            </section>
          </div>
        )}
        <style>{mobileCss}</style>
      </section>
    </AdminLayout>
  );
}

function KpiCard({ title, value, caption, delta, color, icon: Icon }) {
  return (
    <article style={{ ...kpiCard, background: color }}>
      <div style={kpiTop}>
        <span>{title}</span>
        <span style={cardIcon}><Icon size={18} /></span>
      </div>
      <strong style={kpiValue}>{value}</strong>
      <div style={kpiBottom}>
        <span>{caption}</span>
        <span>{delta}</span>
      </div>
    </article>
  );
}

function MiniMetric({ icon: Icon, title, label, tone, color }) {
  return (
    <article style={{ ...miniMetric, background: tone }}>
      <Icon size={18} color={color} />
      <strong style={miniTitle}>{title}</strong>
      <span style={miniLabel}>{label}</span>
    </article>
  );
}

function BidsByGame({ data, total }) {
  const safeData = Array.isArray(data) && data.length ? data : [
    { label: "Jodi", percent: 0, color: "#3b82f6", bets: 0, stake: 0 },
    { label: "Single", percent: 0, color: "#10b981", bets: 0, stake: 0 },
    { label: "Patti", percent: 0, color: "#8b5cf6", bets: 0, stake: 0 },
    { label: "Sangam", percent: 0, color: "#f97316", bets: 0, stake: 0 },
  ];
  const totalStake = safeData.reduce((sum, item) => sum + Number(item.stake || 0), 0);
  let cursor = 0;
  const segments = safeData.map((item) => {
    const start = cursor;
    cursor += Number(item.percent || 0);
    return `${item.color} ${start}% ${cursor}%`;
  }).join(", ");

  return (
    <section style={chartPanel}>
      <header style={chartHeader}>
        <div style={panelTitleWrap}>
          <span style={panelIcon}><Target size={14} /></span>
          <span>
            <strong style={panelTitle}>Bids by Game</strong>
            <small style={panelSubtitle}>Today's distribution</small>
          </span>
        </div>
      </header>
      <div style={donutWrap}>
        <div style={{ ...donut, background: `conic-gradient(${segments || "#e5e7eb 0 100%"})` }}>
          <div style={donutInner}>
            <strong>{formatCompact(total)}</strong>
            <span>Bids</span>
            <em>{formatMoney(totalStake)}</em>
          </div>
        </div>
        <div style={legendGrid}>
          {safeData.map((item) => (
            <div key={item.label} style={legendRow}>
              <span style={{ ...legendDot, background: item.color }} />
              <span>{item.label}</span>
              <strong>{item.percent || 0}%</strong>
              <small style={legendBets}>{`${formatCompact(item.bets || 0)} bets`}</small>
              <b style={legendAmount}>{formatMoney(item.stake || 0)}</b>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CashFlow({ data }) {
  const rows = Array.isArray(data) && data.length ? data : [];
  const maxValue = Math.max(1, ...rows.flatMap((item) => [Number(item.deposits || 0), Number(item.withdrawals || 0)]));

  return (
    <section style={chartPanel}>
      <header style={chartHeader}>
        <div style={panelTitleWrap}>
          <span style={panelIcon}><CreditCard size={14} /></span>
          <span>
            <strong style={panelTitle}>Cash Flow</strong>
            <small style={panelSubtitle}>Deposits vs Withdrawals</small>
          </span>
        </div>
        <div style={chartLegend}>
          <span><i style={{ ...chartDot, background: "#10b981" }} /> Deposits</span>
          <span><i style={{ ...chartDot, background: "#ef4444" }} /> Withdrawals</span>
        </div>
      </header>
      <div style={barChart}>
        {rows.map((item) => (
          <div key={item.label} style={barGroup}>
            <div style={barPair}>
              <span style={{ ...bar, height: `${Math.max(6, (Number(item.deposits || 0) / maxValue) * 150)}px`, background: "#10b981" }} />
              <span style={{ ...bar, height: `${Math.max(6, (Number(item.withdrawals || 0) / maxValue) * 150)}px`, background: "#ef4444" }} />
            </div>
            <small>{item.label}</small>
          </div>
        ))}
        {!rows.length && <div style={emptyPanel}>No cash flow yet</div>}
      </div>
    </section>
  );
}

function TopDwPanel({ analytics, mode, onModeChange, expanded, onToggleExpanded, onWallet }) {
  const tabs = [
    { key: "today_deposit", label: "Today Deposit", rows: analytics.topDepositorsToday || [], tone: "#16a34a" },
    { key: "all_deposit", label: "All Deposit", rows: analytics.topDepositorsAll || [], tone: "#0f9f70" },
    { key: "today_withdraw", label: "Today Withdraw", rows: analytics.topWithdrawersToday || [], tone: "#ef4444" },
    { key: "all_withdraw", label: "All Withdraw", rows: analytics.topWithdrawersAll || [], tone: "#b91c1c" },
  ];
  const selected = tabs.find((tab) => tab.key === mode) || tabs[0];
  const visibleRows = (selected.rows || []).slice(0, expanded ? 10 : 5);

  return (
    <section style={panel}>
      <header style={panelHeader}>
        <div style={panelTitleWrap}>
          <span style={panelIcon}><Users size={14} /></span>
          <span>
            <strong style={panelTitle}>Top D/W Users</strong>
            <small style={panelSubtitle}>Top 10 by deposit or withdraw</small>
          </span>
        </div>
      </header>

      <div style={rankingTabs} className="admin-topdw-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            style={mode === tab.key ? { ...rankingTabActive, borderColor: tab.tone, color: tab.tone } : rankingTab}
            onClick={() => onModeChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={rankingList} className="admin-topdw-list">
        {visibleRows.map((row, index) => (
          <article key={`${selected.key}-${row.user_id}`} style={rankingRow} className="admin-topdw-row">
            <span style={{ ...rankingIndex, background: selected.tone }}>{index + 1}</span>
            <div style={rankingUser}>
              <AdminUserLink userId={row.user_id} name={row.user_name} phone={row.user_phone} />
              <small style={rankingMeta}>{row.count || 0} entries · wallet {formatMoney(row.balance || 0)}</small>
            </div>
            <strong style={{ ...rankingAmount, color: selected.tone }} className="admin-topdw-amount">{formatMoney(row.amount || 0)}</strong>
            <div style={rankingActions} className="admin-topdw-actions">
              <button type="button" style={rankingWalletButton} onClick={() => onWallet(row, "deposit")}>
                <Wallet size={12} /> D
              </button>
              <button type="button" style={rankingWalletButtonRed} onClick={() => onWallet(row, "withdraw")}>
                <Wallet size={12} /> W
              </button>
            </div>
          </article>
        ))}
        {!visibleRows.length && <div style={emptyPanel}>No ranking data yet</div>}
      </div>

      {(selected.rows || []).length > 5 && (
        <button type="button" style={expandButton} onClick={onToggleExpanded}>
          {expanded ? "Show Less" : "Expand Top 10"}
        </button>
      )}
    </section>
  );
}

function formatMoney(value) {
  const amount = Number(value || 0);
  if (Math.abs(amount) >= 100000) return `Rs ${(amount / 100000).toFixed(1)}L`;
  if (Math.abs(amount) >= 1000) return `Rs ${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 1)}K`;
  return `Rs ${amount}`;
}

function formatCompact(value) {
  return new Intl.NumberFormat("en-IN").format(Number(value || 0));
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return `${date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}, ${date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
}

const dashboardWrap = { width: "100%", margin: "0 auto", color: "#0f172a" };
const headingRow = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "18px" };
const titleRow = { display: "flex", alignItems: "center", gap: "10px" };
const title = { margin: 0, fontSize: "28px", lineHeight: 1 };
const liveBadge = { background: "#dcfce7", color: "#16a34a", padding: "4px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: 800 };
const subtitle = { margin: "8px 0 0", color: "#64748b", fontSize: "13px" };
const refreshButton = { width: "40px", height: "40px", display: "grid", placeItems: "center", border: "1px solid #e5e7eb", background: "#fff", color: "#64748b", borderRadius: "12px", cursor: "pointer" };
const kpiGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "16px" };
const kpiCard = { color: "#fff", borderRadius: "18px", padding: "18px", minHeight: "122px", boxShadow: "0 12px 28px rgba(15,23,42,0.08)", boxSizing: "border-box", overflow: "hidden" };
const kpiTop = { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "11px", fontWeight: 800, textTransform: "uppercase", opacity: 0.92 };
const cardIcon = { width: "36px", height: "36px", display: "grid", placeItems: "center", borderRadius: "10px", background: "rgba(255,255,255,0.18)" };
const kpiValue = { display: "block", marginTop: "18px", fontSize: "30px", fontWeight: 800 };
const kpiBottom = { display: "flex", justifyContent: "space-between", marginTop: "14px", fontSize: "12px" };
const miniGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "12px", marginTop: "16px" };
const miniMetric = { minHeight: "92px", display: "grid", alignContent: "center", gap: "8px", padding: "16px", borderRadius: "16px", fontSize: "12px", color: "#64748b" };
const miniTitle = { fontSize: "18px", color: "#0f172a" };
const miniLabel = { fontSize: "12px" };
const analyticsGrid = { display: "grid", gridTemplateColumns: "0.85fr 1.45fr", gap: "16px", marginTop: "18px" };
const chartPanel = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", overflow: "hidden", minHeight: "280px" };
const chartHeader = { height: "58px", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 18px", borderBottom: "1px solid #f1f5f9" };
const donutWrap = { display: "grid", justifyItems: "center", gap: "18px", padding: "18px" };
const donut = { width: "156px", height: "156px", borderRadius: "50%", display: "grid", placeItems: "center" };
const donutInner = { width: "106px", height: "106px", borderRadius: "50%", background: "#fff", display: "grid", placeItems: "center", alignContent: "center", gap: "3px", color: "#111827" };
const legendGrid = { width: "100%", display: "grid", gridTemplateColumns: "1fr", gap: "9px", fontSize: "12px" };
const legendRow = { display: "grid", gridTemplateColumns: "10px 1fr auto", alignItems: "center", gap: "8px", padding: "8px 10px", borderRadius: "12px", background: "#f8fafc", border: "1px solid #eef2f7" };
const legendDot = { width: "9px", height: "9px", borderRadius: "50%" };
const legendBets = { gridColumn: "2 / 3", color: "#64748b", fontWeight: 700, fontSize: "11px" };
const legendAmount = { gridColumn: "3 / 4", color: "#0f172a", fontWeight: 900, fontSize: "12px" };
const chartLegend = { display: "flex", alignItems: "center", gap: "12px", fontSize: "11px", color: "#475569" };
const chartDot = { width: "10px", height: "10px", borderRadius: "50%", display: "inline-block", marginRight: "5px", verticalAlign: "-1px" };
const barChart = { minHeight: "204px", padding: "24px 18px 16px", display: "flex", alignItems: "end", gap: "26px", borderTop: "1px solid #f8fafc" };
const barGroup = { flex: 1, minWidth: "54px", display: "grid", justifyItems: "center", gap: "10px", color: "#94a3b8", fontSize: "11px" };
const barPair = { height: "160px", display: "flex", alignItems: "end", gap: "6px" };
const bar = { width: "22px", borderRadius: "7px 7px 0 0", display: "block" };
const panelGrid = { display: "grid", gridTemplateColumns: "2fr 1fr", gap: "16px", marginTop: "18px" };
const panel = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", minHeight: "260px", overflow: "hidden" };
const panelHeader = { height: "58px", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 18px", borderBottom: "1px solid #f1f5f9" };
const panelTitleWrap = { display: "flex", alignItems: "center", gap: "10px" };
const panelIcon = { width: "30px", height: "30px", display: "grid", placeItems: "center", background: "#f1f5f9", color: "#64748b", borderRadius: "8px" };
const panelTitle = { display: "block", fontSize: "15px", color: "#0f172a" };
const panelSubtitle = { display: "block", marginTop: "2px", color: "#64748b", fontSize: "12px" };
const activityList = { padding: "0 18px 18px", display: "grid", gap: "10px" };
const activityRow = { display: "flex", justifyContent: "space-between", gap: "18px", padding: "14px 0", borderBottom: "1px solid #f1f5f9" };
const activityUserLink = { display: "inline-grid", maxWidth: "220px" };
const activitySub = { marginTop: "4px", color: "#64748b", fontSize: "12px" };
const amountGreen = { color: "#16a34a", fontSize: "14px" };
const amountRed = { color: "#dc2626", fontSize: "14px" };
const emptyPanel = { minHeight: "140px", display: "grid", placeItems: "center", color: "#cbd5e1", fontSize: "14px" };
const rankingTabs = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", padding: "14px 14px 0" };
const rankingTab = { minHeight: "34px", border: "1px solid #e2e8f0", borderRadius: "10px", background: "#fff", color: "#475569", fontSize: "11px", fontWeight: 900, cursor: "pointer" };
const rankingTabActive = { ...rankingTab, background: "#f8fafc" };
const rankingList = { display: "grid", gap: "8px", padding: "14px" };
const rankingRow = { display: "grid", gridTemplateColumns: "30px minmax(0, 1fr) auto", gap: "9px", alignItems: "center", padding: "10px", border: "1px solid #eef2f7", borderRadius: "14px", background: "#f8fafc" };
const rankingIndex = { width: "28px", height: "28px", borderRadius: "10px", color: "#fff", display: "grid", placeItems: "center", fontSize: "12px", fontWeight: 900 };
const rankingUser = { minWidth: 0, display: "grid", gap: "3px" };
const rankingMeta = { color: "#64748b", fontSize: "10px", fontWeight: 800 };
const rankingAmount = { fontSize: "13px", fontWeight: 900, gridColumn: "3 / 4", whiteSpace: "nowrap" };
const rankingActions = { gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "7px" };
const rankingWalletButton = { minHeight: "32px", border: 0, borderRadius: "10px", background: "#dcfce7", color: "#15803d", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "5px", fontSize: "11px", fontWeight: 900, cursor: "pointer" };
const rankingWalletButtonRed = { ...rankingWalletButton, background: "#fee2e2", color: "#dc2626" };
const expandButton = { margin: "0 14px 14px", minHeight: "38px", border: "1px solid #dbeafe", borderRadius: "12px", background: "#eff6ff", color: "#1d4ed8", fontWeight: 900, cursor: "pointer" };
const walletBackdrop = { position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15,23,42,.42)", display: "grid", placeItems: "center", padding: "18px" };
const walletModal = { width: "min(420px, 94vw)", borderRadius: "18px", background: "#fff", border: "1px solid #e5e7eb", boxShadow: "0 24px 64px rgba(15,23,42,.22)", overflow: "hidden" };
const walletModalHeader = { minHeight: "54px", padding: "0 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #eef2f7", color: "#0f172a" };
const walletCloseButton = { width: "34px", height: "34px", border: 0, borderRadius: "10px", background: "#f8fafc", color: "#64748b", display: "grid", placeItems: "center", cursor: "pointer" };
const walletBody = { padding: "16px", display: "grid", gap: "12px" };
const walletModeRow = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" };
const walletModeButton = { minHeight: "40px", border: "1px solid #dbe4f0", borderRadius: "12px", background: "#fff", color: "#475569", fontWeight: 900, cursor: "pointer" };
const activeWalletMode = { ...walletModeButton, background: "#dcfce7", borderColor: "#86efac", color: "#15803d" };
const activeWalletModeRed = { ...walletModeButton, background: "#fee2e2", borderColor: "#fecaca", color: "#dc2626" };
const walletInput = { height: "44px", border: "1px solid #dbe4f0", borderRadius: "12px", padding: "0 13px", outline: 0, fontWeight: 800 };
const walletSubmitButton = { minHeight: "44px", border: 0, borderRadius: "12px", background: "#2563eb", color: "#fff", fontWeight: 900, cursor: "pointer" };

const mobileCss = `
@media (max-width: 640px) {
  .admin-dashboard-kpis {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 8px !important;
  }
  .admin-dashboard-kpis article {
    min-height: 84px !important;
    border-radius: 14px !important;
    padding: 9px 7px !important;
  }
  .admin-dashboard-kpis article > div:first-child {
    font-size: 8px !important;
    line-height: 1.05 !important;
    gap: 4px !important;
  }
  .admin-dashboard-kpis article > strong {
    margin-top: 9px !important;
    font-size: 16px !important;
    line-height: 1 !important;
    white-space: nowrap;
  }
  .admin-dashboard-kpis article > div:last-child {
    margin-top: 8px !important;
    font-size: 8px !important;
    gap: 4px !important;
  }
  .admin-dashboard-kpis article span[style*="36px"] {
    width: 24px !important;
    height: 24px !important;
    border-radius: 8px !important;
  }
  .admin-dashboard-minis {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 8px !important;
  }
  .admin-dashboard-minis article {
    min-height: 72px !important;
    padding: 8px 6px !important;
    border-radius: 13px !important;
    gap: 4px !important;
    justify-items: center;
    text-align: center;
  }
  .admin-dashboard-minis article strong {
    font-size: 13px !important;
    line-height: 1 !important;
  }
  .admin-dashboard-minis article span {
    font-size: 9px !important;
    line-height: 1.1 !important;
  }
  .admin-dashboard-analytics,
  .admin-dashboard-panels {
    grid-template-columns: 1fr !important;
    gap: 10px !important;
    margin-top: 10px !important;
  }
  .admin-topdw-tabs {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 6px !important;
    padding: 10px 10px 0 !important;
  }
  .admin-topdw-tabs button {
    min-height: 34px !important;
    padding: 0 6px !important;
    font-size: 9px !important;
    line-height: 1.05 !important;
    white-space: normal !important;
  }
  .admin-topdw-list {
    padding: 10px !important;
    gap: 7px !important;
  }
  .admin-topdw-row {
    grid-template-columns: 24px minmax(0, 1fr) auto !important;
    gap: 7px !important;
    padding: 8px !important;
    border-radius: 12px !important;
  }
  .admin-topdw-amount {
    font-size: 11px !important;
    max-width: 74px !important;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .admin-topdw-actions {
    grid-column: 1 / -1 !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 6px !important;
  }
  .admin-topdw-actions button {
    min-height: 28px !important;
    border-radius: 8px !important;
    font-size: 10px !important;
  }
  .admin-keyboard-wallet-overlay {
    place-items: start center !important;
    align-items: start !important;
    overflow-y: auto !important;
    overscroll-behavior: contain;
    padding: calc(env(safe-area-inset-top) + 8px) 8px calc(env(safe-area-inset-bottom) + 12px) !important;
    box-sizing: border-box !important;
    -webkit-overflow-scrolling: touch;
  }
  .admin-keyboard-wallet-modal {
    width: min(100%, 420px) !important;
    max-width: 100% !important;
    max-height: calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 16px) !important;
    border-radius: 16px !important;
    display: grid !important;
    grid-template-rows: auto minmax(0, 1fr) !important;
    overflow: hidden !important;
  }
  @supports not (height: 100dvh) {
    .admin-keyboard-wallet-modal {
      max-height: calc(100vh - 16px) !important;
    }
  }
  .admin-keyboard-wallet-header {
    min-height: 48px !important;
    padding: 0 12px !important;
  }
  .admin-keyboard-wallet-body {
    min-height: 0 !important;
    overflow-y: auto !important;
    padding: 12px !important;
    -webkit-overflow-scrolling: touch;
  }
  .admin-keyboard-wallet-modes {
    gap: 6px !important;
  }
  .admin-keyboard-wallet-modes button {
    min-height: 38px !important;
    font-size: 12px !important;
  }
  .admin-keyboard-wallet-input {
    height: 42px !important;
    border-radius: 11px !important;
  }
  .admin-keyboard-wallet-submit {
    position: sticky !important;
    bottom: -12px !important;
    z-index: 5;
    min-height: 44px !important;
    margin: 2px 0 -12px !important;
    padding-bottom: env(safe-area-inset-bottom) !important;
    box-shadow: 0 -10px 18px rgba(15,23,42,0.08);
  }
}
`;

export default AdminDashboard;
